import React, { ReactNode } from "react";
import {
  Box,
  CardContent,
  Divider,
  Typography,
  useTheme,
  Link,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import EmptyWidgetImage from "../../assets/empty-widget.svg";
import { ErrorOutline } from "@mui/icons-material";

const ContentErrorContactAccountTeam = ({
  accountTeamUrl,
}: {
  accountTeamUrl: string;
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: (theme) => theme.spacing(3),
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <ErrorOutline
          aria-label="Error icon"
          color="disabled"
          fontSize="medium"
        />
        <Typography variant="body1" color="textSecondary">
          Unexpected Error Occurred
        </Typography>
      </Box>
      <Typography variant="body2" color="textSecondary">
        Refresh this page or try again later. If the problem persists, contact
        your
        <Link
          variant="body2"
          href={accountTeamUrl}
          sx={{ ml: 0.5 }}
          aria-label="Account Team (opens in the current tab)"
        >
          Account Team
        </Link>
      </Typography>
    </Box>
  );
};

const ContentErrorDefault = () => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        padding: ".5rem",
      }}
    >
      <Typography variant="h4">Error</Typography>
    </Box>
  );
};

const ContentError = ({ accountTeamLink }: { accountTeamLink?: string }) => {
  if (accountTeamLink) {
    return <ContentErrorContactAccountTeam accountTeamUrl={accountTeamLink} />;
  }
  return <ContentErrorDefault />;
};

export function WidgetContent({
  isEmpty,
  error,
  children,
  noPadding,
  containerSizeInPixels,
  errorContactAccountTeamLink,
  hideWidgetContentOnError,
}: {
  isEmpty?: boolean;
  error?: boolean;
  children?: ReactNode;
  noPadding?: boolean;
  containerSizeInPixels?: number;
  errorContactAccountTeamLink?: string;
  hideWidgetContentOnError?: boolean;
}) {
  const theme = useTheme();

  const style: any = {};

  if (isEmpty) {
    style.background =
      `url(${EmptyWidgetImage})` + alpha(theme.palette.background.default, 0.5);
    style.backgroundRepeat = "no-repeat";
    style.backgroundPosition = "center";
    style.backgroundSize = "cover";
  }

  if (containerSizeInPixels) {
    style.maxHeight = containerSizeInPixels;
  }

  const handleChildren = (children: ReactNode | any[]) => {
    if (error && hideWidgetContentOnError) {
      return null;
    }
    if (Array.isArray(children)) {
      return children.map((item, index) => {
        const key = item + index;
        return (
          <div key={key}>
            <div style={{ padding: noPadding ? 0 : theme.spacing(2) }}>
              {item}
            </div>
            {index < children.length - 1 && <Divider />}
          </div>
        );
      });
    }
    return (
      <div style={{ padding: noPadding ? 0 : theme.spacing(2) }}>
        {children}
      </div>
    );
  };

  return (
    <CardContent sx={style}>
      {handleChildren(children)}
      {error && (
        <ContentError accountTeamLink={errorContactAccountTeamLink} />
      )}
    </CardContent>
  );
}
